import { CommonModule } from '@angular/common';
import { Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ProjectAdminResponse,
  ProjectBaseRequest,
  UserSimpleResponse,
} from '@app/models/admin.model';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { UserService } from '@app/services/admin/user.service';
import { ToastService } from '@app/services/toast.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { pickBaseRequest } from './sales-contact-card.helper';

@Component({
  selector: 'app-sales-contact-card',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './sales-contact-card.component.html',
  styleUrl: './sales-contact-card.component.scss',
})
export class SalesContactCardComponent {
  private readonly userService = inject(UserService);
  private readonly projectService = inject(ProjectAdminService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  visible = input<boolean>(true);
  projectId = input.required<string>();
  project = input.required<ProjectAdminResponse>();
  updated = output<void>();

  readonly dialogOpen = signal(false);
  readonly saving = signal(false);

  readonly contacts = signal<UserSimpleResponse[]>([]);
  readonly selectedContactId = signal<string | number | null>(null);

  openDialog(): void {
    this.dialogOpen.set(true);
    if (this.contacts().length === 0) {
      this.userService.getProjectAdmins().subscribe({
        next: (list) => this.contacts.set(list ?? []),
        error: () => this.contacts.set([]),
      });
    }
  }

  closeDialog(): void {
    if (this.saving()) return;
    this.dialogOpen.set(false);
  }

  onModelChange(value: string | number | null): void {
    this.selectedContactId.set(value);
  }

  confirmAssign(): void {
    const selected = this.selectedContactId();
    if (!selected) return;

    const p = this.project();
    const body: ProjectBaseRequest = {
      ...pickBaseRequest(p),
      contactUserId: String(selected),
    };

    this.saving.set(true);
    this.projectService
      .updateProjectAdmin(this.projectId(), body)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant(
              'admin.projectIdeas.salesContactCard.toasts.assigned'
            )
          );
          this.updated.emit();
          this.dialogOpen.set(false);
        },
        error: () => {
          this.toast.error(
            this.translate.instant(
              'admin.projectIdeas.salesContactCard.toasts.assignFailed'
            )
          );
        },
      });
  }
}
